import { useCallback, useEffect, useRef, useState } from "react";
import { useCampaignTheme } from "hooks/useCampaignTheme";
import { useLoaderSettled } from "hooks/useLoaderSettled";
import {
  shouldShowSplashIntro,
  SPLASH_INTRO_PLAYED_SESSION_KEY,
} from "utils/campaignTheme";
import {
  campaignGA,
  SplashIntroEndReason,
} from "utils/campaignTheme/analytics";
import { loadSessionState, saveSessionState } from "utils/sessionStorage";

export const SPLASH_INTRO_EXIT_MS = 250;

// How long the global loader must stay hidden before the intro hands over.
const LOADER_SETTLE_MS = 300;

type Phase = "playing" | "exiting" | "done";

/**
 * Drives the campaign intro shown once per app open, right after the native
 * splash. It covers app loading so the Wishlink loader never shows: once the
 * animation has loaded it plays for at least `minDurationMs`, then hands over
 * when the global loader has settled. A load error, the `maxDurationMs` cap
 * (counted from mount) or the campaign being switched off end it early.
 * Reports `campaign_intro_view` / `campaign_intro_end` to GA.
 */
export const useSplashIntro = () => {
  const campaign = useCampaignTheme();
  const splashIntro = campaign?.slots.splashIntro;
  const isLoaderSettled = useLoaderSettled(LOADER_SETTLE_MS);
  const [hasAnimationStarted, setHasAnimationStarted] = useState(false);
  const [hasMinElapsed, setHasMinElapsed] = useState(false);

  // Kept from mount so the end event still has them if the kill switch
  // clears the campaign mid-intro.
  const campaignIdRef = useRef(campaign?.id);
  const mountedAtRef = useRef(Date.now());
  const endReasonRef = useRef<SplashIntroEndReason | null>(null);
  const wasAnimationShownRef = useRef(false);

  const [phase, setPhase] = useState<Phase>(() =>
    shouldShowSplashIntro({
      campaign,
      isNativeApp: !!(window as any).ReactNativeWebView,
      pathname: window.location.pathname,
      hasPlayedThisSession: !!loadSessionState(SPLASH_INTRO_PLAYED_SESSION_KEY),
    })
      ? "playing"
      : "done"
  );

  const end = useCallback((reason: SplashIntroEndReason) => {
    endReasonRef.current ??= reason;
    setPhase((current) => (current === "playing" ? "exiting" : current));
  }, []);

  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  // Ignores a load that lands after the intro has already ended (e.g. during
  // the fade-out after the cap), so it is neither counted nor reported.
  const markAnimationStarted = useCallback(() => {
    if (phaseRef.current !== "playing") return;
    wasAnimationShownRef.current = true;
    setHasAnimationStarted(true);
  }, []);

  const handleLoadError = useCallback(() => end("load_error"), [end]);

  useEffect(() => {
    if (phase !== "playing") return undefined;
    // Mark straight away so a reload mid-animation doesn't replay it.
    saveSessionState(SPLASH_INTRO_PLAYED_SESSION_KEY, true);
    if (!splashIntro) {
      end("disabled");
      return undefined;
    }
    const maxTimer = setTimeout(
      () => end("max_duration"),
      splashIntro.maxDurationMs
    );
    return () => clearTimeout(maxTimer);
  }, [phase, splashIntro, end]);

  // The minimum runs from when the animation is on screen, not from mount:
  // the player chunk and the .lottie can still be downloading on a cold start.
  useEffect(() => {
    if (phase !== "playing" || !hasAnimationStarted || !splashIntro) {
      return undefined;
    }
    const minTimer = setTimeout(
      () => setHasMinElapsed(true),
      splashIntro.minDurationMs
    );
    return () => clearTimeout(minTimer);
  }, [phase, hasAnimationStarted, splashIntro]);

  useEffect(() => {
    if (phase === "playing" && hasMinElapsed && isLoaderSettled) {
      end("app_loaded");
    }
  }, [phase, hasMinElapsed, isLoaderSettled, end]);

  useEffect(() => {
    if (hasAnimationStarted && campaignIdRef.current) {
      campaignGA.introView({ campaignId: campaignIdRef.current });
    }
  }, [hasAnimationStarted]);

  useEffect(() => {
    if (phase !== "exiting") return undefined;
    if (campaignIdRef.current && endReasonRef.current) {
      campaignGA.introEnd({
        campaignId: campaignIdRef.current,
        reason: endReasonRef.current,
        wasAnimationShown: wasAnimationShownRef.current,
        durationMs: Date.now() - mountedAtRef.current,
      });
    }
    const timer = setTimeout(() => setPhase("done"), SPLASH_INTRO_EXIT_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  return {
    splashIntro: phase === "done" ? undefined : splashIntro,
    isExiting: phase === "exiting",
    handleLoadError,
    markAnimationStarted,
  };
};
